import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Subject, Subscription, takeUntil } from 'rxjs';

import { AccommodationsService } from './accommodations.service';
import { AccommodationsDataService } from './accommodations-data.service';
import { Month } from './accommodations.model';

import { AccommodationsDestinationSelectComponent } from './accommodations-destination-select/accommodations-destination-select.component';
import { AccommodationsProductsSelectComponent } from './accommodations-products-select/accommodations-products-select.component';
import { DateService } from '../common/date.service';
import { LoaderService } from '../common/loader/loader.service';
import { AccommodationsProductWithoutRoomsComponent } from './accommodations-product-without-rooms/accommodations-product-without-rooms.component';

@Component({
	selector: 'app-accommodations',
	templateUrl: './accommodations.component.html',
	styleUrls: ['./accommodations.component.scss'],
})
export class AccommodationsComponent implements OnInit, OnDestroy {
	protected months: Month[] = [];
	private readonly subscriptions = new Subscription();
	private readonly destroy$: Subject<void> = new Subject();
	private loadBefore = 4;
	showLegend = false;

	constructor(
		private readonly dialog: MatDialog,
		private readonly route: ActivatedRoute,
		private readonly date: DateService,
		protected service: AccommodationsService,
		protected dataService: AccommodationsDataService,
		private readonly loader: LoaderService,
	) {}

	ngOnInit(): void {
		if (!this.route.snapshot.queryParams['date']) {
			this.service.queryParmSet('date', this.date.now().date);
		}
		this.loader.show();
		this.dataService.loadLocations().then(() => {
			this.subscribe();
		});
		this.dataService.loadRoomTypes().then(() => {
			this.subscribe();
		});
		this.dataService.loadProductsAll().then(() => {
			this.subscribe();
		});
		this.dataService.loadNoname().then(() => {
			this.subscribe();
		});
		this.dataService.loadAccomodations();

		this.service.triggerRefreshingWithoutRooms$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.dataService.loadReservationWithOutRoom();
		});
		this.service.triggerRefreshingView$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.dataService.loadAvailabilities();
		});
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
		this.destroy$.next();
		this.destroy$.complete();
	}

	private subscribe() {
		this.loadBefore--;
		if (this.loadBefore) {
			return;
		}
		this.loader.hide();
		this.subscriptions.add(
			this.route.queryParams.subscribe(async (parms) => {
				// Date changed
				if (parms.date && parms.date != this.service.selected.getValue().date) {
					this.service.selectedSet<string>('date', parms.date);
					this.months = this.service.generateMonths();
					this.dataService.loadAvailabilities();
					this.dataService.loadAvailabilitiesNoname();
				}
				// Destination changed
				if (parms.destination && parms.destination != this.service.selected.getValue().destination?.id) {
					const destination = this.service.data.destinations.find((obj) => obj.id == parms.destination);
					this.service.selectedSet('destination', destination);
					await this.dataService.loadProducts();
					const products = this.service.data.products.map((obj) => obj.id).join(',');
					this.service.queryParmSet('products', products);
				}
				// Products changed
				if (parms.products && parms.products != this.service.selected.getValue().products.join(',')) {
					const products = parms.products.split(',').map((obj) => Number(obj));
					this.service.selectedSet<number[]>('products', products);
					this.service.productsSelectedRefresh();
					await this.dataService.loadAccomodations();
				}
			}),
		);
	}

	protected productsSelect(): void {
		const dialog = this.dialog
			.open(AccommodationsProductsSelectComponent)
			.afterClosed()
			.subscribe((result) => {
				dialog.unsubscribe();
				if (!result) {
					return;
				}
				this.service.queryParmSet('products', result.join(','));
			});
	}

	protected destinationSelect(): void {
		const dialog = this.dialog
			.open(AccommodationsDestinationSelectComponent)
			.afterClosed()
			.subscribe((result) => {
				dialog.unsubscribe();
				if (!result) {
					return;
				}
				this.service.queryParmSet('destination', result);
			});
	}

	protected reservationMoveCancel(): void {
		this.service.reservationMove = null;
	}

	protected noNameShow(): void {
		if (!this.service.show.noname) {
			this.dataService.loadNoname().then(() => {
				this.subscribe();
			});
		}
	}

	protected withOut(): void {
		const dialogRef = this.dialog.open(AccommodationsProductWithoutRoomsComponent, {
			disableClose: false,
			width: '1200px',
		});
	}
}
